import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
} from '@mui/material';
import { toast } from 'react-toastify';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';

// Format date to dd/mm/yyyy
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date)) return 'N/A';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

// Fix broken URLs
const fixImageUrl = (url) => {
  if (!url) return null;
  return url.replace(/^http:\/([^/])/, 'http://$1');
};

// Reusable Label-Value Row
const LabelValue = ({ label, value }) => (
  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
    <Typography
      fontWeight="bold"
      color="text.secondary"
      sx={{
        whiteSpace: 'nowrap',
        fontSize: { xs: '0.99rem', sm: '1rem' },
      }}
    >
      {label}:
    </Typography>
    <Typography sx={{ fontSize: { xs: '0.99rem', sm: '1rem' } }}>
      {value || 'N/A'}
    </Typography>
  </Box>
);

const TaskStDialog = ({ open, onClose, ticket, onSubmit, setSelectedRows, setSelectedCategoryId, getStatusChip }) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [status, setStatus] = useState('');
  const [remark, setRemark] = useState('');
  const [material, setMaterial] = useState('');
  const [totalCost, setTotalCost] = useState('');
  const [zoomOpen, setZoomOpen] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');

  // For uploaded image zoom
  const [zoomImageOpen, setZoomImageOpen] = useState(false);
  const [zoomImageUrl, setZoomImageUrl] = useState('');


  useEffect(() => {
    if (zoomOpen || zoomImageOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [zoomOpen, zoomImageOpen]);
  useEffect(() => {
    if (ticket && open) {
      if (ticket.TicketSt === 'R') {
        setStatus('resolved');
      } else if (ticket.TicketSt === 'C') {
        setStatus('closed');
      } else {
        setStatus('');
      }
      setRemark('');
      setMaterial('');
      setTotalCost('');
      setUploadedImage(null);
      setUploadedFileName('');
      setZoomImageOpen(false);
    }
  }, [ticket, open]);

  if (!ticket || !open) return null;

  const imageUrl = fixImageUrl(ticket.ImgName);
  const hasImage = !!imageUrl;

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedImage(event.target.result);
      setUploadedFileName(file.name);
    };
    reader.onerror = () => toast.error('Failed to upload image');
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
    setUploadedFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openZoom = (url) => {
    setZoomImageUrl(url);
    setZoomImageOpen(true);
  };

  const handleSubmit = () => {
    if (!status) {
      toast.warning('Please select a status');
      return;
    }

    onSubmit(
      ticket.TicketId,
      status,
      remark,
      uploadedImage || '',
      uploadedFileName || '',
      material,
      totalCost
    );

    // ADD THESE LINES — CRITICAL!
    setSelectedRows([]);           // ← Clear all checkboxes
    setSelectedCategoryId(null);   // ← Reset category lock

    onClose();
  };

  const handleCancel = () => {
    setStatus('');
    setRemark('');
    setUploadedImage(null);
    setUploadedFileName('');
    // if (typeof setSelectedRows === 'function') setSelectedRows([]);
    // if (typeof setSelectedCategoryId === 'function') setSelectedCategoryId(null);

    setSelectedRows([]);           // ← Already good
    setSelectedCategoryId(null);
    onClose();
  };

  const radioLabelStyle = {
    m: 0,
    mr: { xs: 0.5, sm: 0 },
    '& .MuiFormControlLabel-label': {
      fontSize: { xs: '0.88rem', sm: '1rem' },
      pl: 0,
    },
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: 6,
            width: { xs: '95%', sm: '75%', md: '65%' },
            maxWidth: { xs: '98%', sm: 450, md: 590 },
            m: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle
          sx={{
            pt: 1,
            pb: 1,
            px: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 'bold',
            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            color: '#fff',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.4rem' }, color: '#fff' }}>
            Task Status
          </Typography>
          <Box sx={{ ml: 2 }}>
            {getStatusChip(ticket.TicketSt)}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: { xs: 1.5, sm: 3.5 } }}>
          {/* TOP ROW: Fields + Image */}
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: { xs: 0, sm: 1, md: 1 }, alignItems: 'flex-start', mb: { xs: 0, sm: -3, md: -3 } }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
              <Box mb={1} mt={1}><LabelValue label="Ticket No" value={ticket.TicketNo} /></Box>
              <Box mb={1}><LabelValue label="RaisedBy" value={ticket.Name} /></Box>
              <Box mb={1} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row', md: 'row' }, gap: { xs: 1, sm: 0, md: 0 } }}>
                <LabelValue label="Date" value={formatDate(ticket.TktDate)} />
                <Box sx={{ display: { xs: 'none', sm: 'block' }, mx: 1 }}>|</Box>
                <LabelValue label="Time" value={formatTime(ticket.TktDate)} />
              </Box>
              {/* <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <LabelValue label="Service Name" value={ticket.ServiceName} />
              </Box> */}
            </Box>

            <Box sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'flex-start', minWidth: { xs: 100, sm: 130 }, mt: '10px'
            }}>

              <Box
                sx={{
                  width: { xs: 80, sm: 90 },
                  height: { xs: 90, sm: 90 },
                  borderRadius: 2,
                  border: hasImage ? `2px solid ${theme.palette.primary.main}` : `2px dashed ${theme.palette.grey[400]}`,
                  overflow: 'hidden',
                  position: 'relative',
                  bgcolor: hasImage ? 'transparent' : 'grey.50',
                  boxShadow: hasImage ? 2 : 1,
                  cursor: hasImage ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => hasImage && setZoomOpen(true)}
              >
                {hasImage ? (
                  <Box
                    component="img"
                    src={imageUrl}
                    alt="Ticket"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                    sx={{
                      width: '115%',
                      height: '115%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      textAlign: 'center',
                    }}
                  >
                    No Image
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* Remaining Fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ mt: { xs: 0, sm: 3, md: 3 } }} >
              <LabelValue label="Service Name" value={ticket.ServiceName} />
            </Box>
            <Box mb={1} sx={{ mt: { xs: 0, sm: 0, md: 1.5 } }}>
              <LabelValue label="Category" value={ticket.CategoryName} />
            </Box>
            <Box mb={1}><LabelValue label="SubCategory" value={ticket.SubCategoryName} /></Box>
            <Box mb={1}><LabelValue label="Service Provider" value={ticket.ServiceProvider} /></Box>

            {/* Status + Upload Image Text (NOW ON CLOSED) */}
            <Box sx={{ mt: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 2, sm: 3 },
                }}
              >
                {/* ---------- Status label + all radios in one row on desktop ---------- */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    gap: { xs: 0.5, sm: 0.1 },
                    width: '100%',
                  }}
                >
                  <Typography
                    fontWeight="bold"
                    color="text.secondary"
                    sx={{
                      whiteSpace: 'nowrap',
                      fontSize: { xs: '0.99rem', sm: '1rem' },
                      mr: { xs: 0.1, sm: 0.1 },
                    }}
                  >
                    Status:
                  </Typography>
                  <RadioGroup
                    row
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    sx={{
                      display: 'flex',
                      gap: { xs: 0.1, sm: 0.1 },
                      flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    }}
                  >
                    <FormControlLabel
                      value="resolved"
                      control={<Radio size="small" />}
                      label="Resolved"
                      sx={radioLabelStyle}
                    />
                    <FormControlLabel
                      value="hold"
                      control={<Radio size="small" />}
                      label="Hold"
                      sx={radioLabelStyle}
                    />
                    <FormControlLabel
                      value="inprogress"
                      control={<Radio size="small" />}
                      label="InProgress"
                      sx={radioLabelStyle}
                    />
                    <FormControlLabel
                      value="closed"
                      control={<Radio size="small" />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Typography sx={{ fontSize: { xs: '0.88rem', sm: '1rem' } }}>
                            Closed
                          </Typography>
                          <IconButton component="label" size="small" color="primary">
                            <PhotoCameraIcon fontSize="small" />
                            <input
                              ref={fileInputRef}
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={handleImageUpload}
                            />
                          </IconButton>
                          <Typography
                            sx={{
                              fontSize: { xs: '0.78rem', sm: '0.88rem' },
                              color: theme.palette.primary.main,
                              fontWeight: 'medium',
                            }}
                          >
                            UploadImg
                          </Typography>
                        </Box>
                      }
                      sx={{
                        ...radioLabelStyle,
                        m: 0,
                      }}
                    />
                  </RadioGroup>
                </Box>
              </Box>
            </Box>
            {/* 1. REMARK - Now appears BEFORE Material & Cost */}
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Remark"
                size="small"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Enter remark"
                fullWidth
                multiline
                rows={isMobile ? 1.1 : 1.1}
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: { xs: '0.9rem', sm: '0.95rem' },
                    '& .MuiInputBase-input': { overflowY: 'auto', maxHeight: isMobile ? 80 : 140 },
                  },
                }}
              />
            </Box>


            {/* ==== Remark + Resolved Proof Image ==== */}
            <Box sx={{ mt: 1.5 }}>
              {/* ----- MOBILE LAYOUT ----- */}
              {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>

                  {/* Uploaded Image (centered) */}
                  {uploadedImage && (
                    <Box sx={{ flexShrink: 0, textAlign: 'center' }}>
                      <Typography
                        variant="caption"
                        color="success.main"
                        fontWeight="bold"
                        display="block"
                        mb={0.5}
                      >
                        Resolved Proof
                      </Typography>

                      <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        <Box
                          component="img"
                          src={uploadedImage}
                          alt="Resolved proof"
                          onClick={() => openZoom(uploadedImage)}
                          sx={{
                            width: { xs: 120, sm: 90 },
                            height: { xs: 130, sm: 90 },
                            borderRadius: 2,
                            objectFit: 'cover',
                            cursor: 'pointer',
                            border: `2px solid ${theme.palette.success.main}`,
                            boxShadow: 4,
                            transition: 'all 0.2s',
                            '&:hover': { transform: 'scale(1.05)', boxShadow: 8 },
                          }}
                        />

                        {/* Remove */}
                        <Tooltip title="Remove Image" arrow>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={handleRemoveImage}
                            sx={{
                              position: 'absolute',
                              top: -10,
                              right: -10,
                              bgcolor: 'white',
                              boxShadow: 3,
                              '&:hover': { bgcolor: 'error.light', color: 'white' },
                            }}
                          >
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* Zoom */}
                        <Tooltip title="Zoom Image" arrow>
                          <IconButton
                            size="small"
                            onClick={() => openZoom(uploadedImage)}
                            sx={{
                              position: 'absolute',
                              bottom: 6,
                              right: 6,
                              bgcolor: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              '&:hover': { bgcolor: 'primary.main' },
                            }}
                          >
                            <ZoomInIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mt={0.5}
                      >
                        {uploadedFileName}
                      </Typography>
                    </Box>
                  )}

                  {/* Material Detail & Total Cost - Always visible */}
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, width: "100%" }}>
                    <TextField
                      label="Material Detail"
                      size="small"
                      fullWidth
                      multiline
                      rows={2}
                      value={material}
                      onChange={(e) => setMaterial(e.target.value)}
                      placeholder="1.Fevikwick
                  2.Pipe"
                      sx={{
                        '& .MuiInputBase-input': { fontSize: '0.95rem' },
                      }}
                    />
                    <TextField
                      label="Total Cost (₹)"
                      size="small"
                      type="number"
                      value={totalCost}
                      onChange={(e) => setTotalCost(e.target.value)}
                      placeholder="1250"
                      fullWidth
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>₹</Typography>,
                      }}
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.95rem' } }}
                    />
                  </Box>
                </Box>
              ) : (
                /* ----- DESKTOP / TABLET LAYOUT (unchanged) ----- */
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'flex-start' }}>
                  {/* LEFT: Remark */}
                  {/*Material & Total Cost */}
                  <Box sx={{ mt: 0, mb: 0, width: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, width: '100%', mb: 1 }}>
                      <TextField
                        label="Material Detail"
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                        value={material}
                        onChange={(e) => setMaterial(e.target.value)}
                        placeholder="Fevikwick, Pipe, Mold Pipe, Tape"
                        sx={{
                          '& .MuiInputBase-input': {
                            fontSize: '0.95rem'
                          }
                        }}
                      />
                    </Box>
                    <TextField
                      label="Total Cost (₹)"
                      size="small"
                      type="number"
                      value={totalCost}
                      onChange={(e) => setTotalCost(e.target.value)}
                      placeholder="1250"
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>₹</Typography>,
                      }}
                      fullWidth
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.95rem' } }}
                    />
                  </Box>

                  {/* RIGHT: Uploaded Image (only if exists) */}
                  {uploadedImage && (
                    <Box sx={{ flexShrink: 0, alignSelf: 'flex-end', pb: { xs: 1, sm: 0 } }}>
                      <Box sx={{ position: 'relative', display: 'inline-block' }}>
                        <Box
                          component="img"
                          src={uploadedImage}
                          alt="Resolved proof"
                          onClick={() => openZoom(uploadedImage)}
                          sx={{
                            width: { xs: 90, sm: 90 },
                            height: { xs: 100, sm: 90 },
                            borderRadius: 2,
                            objectFit: 'cover',
                            cursor: 'pointer',
                            border: `2px solid ${theme.palette.success.main}`,
                            boxShadow: 4,
                            transition: 'all 0.2s',
                            '&:hover': { transform: 'scale(1.05)', boxShadow: 8 },
                          }}
                        />

                        {/* Remove */}
                        <Tooltip title="Remove Image" arrow>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={handleRemoveImage}
                            sx={{
                              position: 'absolute',
                              top: -10,
                              right: -10,
                              bgcolor: 'white',
                              boxShadow: 3,
                              '&:hover': { bgcolor: 'error.light', color: 'white' },
                            }}
                          >
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* Zoom */}
                        <Tooltip title="Zoom Image" arrow>
                          <IconButton
                            size="small"
                            onClick={() => openZoom(uploadedImage)}
                            sx={{
                              position: 'absolute',
                              bottom: 6,
                              right: 6,
                              bgcolor: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              '&:hover': { bgcolor: 'primary.main' },
                            }}
                          >
                            <ZoomInIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mt={0.5}
                        textAlign="center"
                      >
                        {uploadedFileName}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 1, pt: 1, mt: -1, justifyContent: 'flex-end', gap: 1.3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!status || !remark}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold',
              px: 2,
              fontSize: { xs: '0.99rem', sm: '1rem' },
              minWidth: 110,
            }}
          >
            Submit
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleCancel}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold',
              px: 2,
              fontSize: { xs: '0.99rem', sm: '1rem' },
              minWidth: 110,
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Original Ticket Image Zoom */}
      {/* === ZOOM DIALOG 1: Original Ticket Image - CENTERED LIKE BEFORE === */}
      <Dialog
        open={zoomOpen}
        onClose={() => setZoomOpen(false)}
        maxWidth="md"
        fullWidth
        BackdropProps={{
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.95)' },
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden',
            m: 2,
          },
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={() => setZoomOpen(false)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            color: 'black',
            '&:hover': { bgcolor: 'white' },
            zIndex: 10,
            boxShadow: 3,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Centered Image Container */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '90vh',
            cursor: 'zoom-out',
          }}
          onClick={() => setZoomOpen(false)}
        >
          <Box
            component="img"

            src={imageUrl}
            alt="Zoomed Ticket"
            onError={(e) => {
              e.target.style.display = 'none'; // Hide broken image
            }}
            sx={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              borderRadius: 2,
              boxShadow: 10,
              userSelect: 'none',
            }}
          />
        </Box>
      </Dialog>

      {/* === ZOOM DIALOG 2: Uploaded Image - CENTERED LIKE BEFORE === */}
      <Dialog
        open={zoomImageOpen}
        onClose={() => setZoomImageOpen(false)}
        maxWidth="md"
        fullWidth
        BackdropProps={{
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.95)' },
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden',
            m: 2,
          },
        }}
      >
        <IconButton
          onClick={() => setZoomImageOpen(false)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            color: 'black',
            '&:hover': { bgcolor: 'white' },
            zIndex: 10,
            boxShadow: 3,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '90vh',
            cursor: 'zoom-out',
          }}
          onClick={() => setZoomImageOpen(false)}
        >
          <Box
            component="img"
            src={zoomImageUrl}
            alt="Zoomed Resolution Proof"
            sx={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              borderRadius: 2,
              boxShadow: 10,
              userSelect: 'none',
            }}
          />
        </Box>
      </Dialog>
    </>
  );
};

export default TaskStDialog;